import React, { useState, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Plus } from 'lucide-react';
import Button from 'components/utils/Button';
import DropDown from 'components/utils/DropDown';
import TextBox from 'components/utils/TextBox';
import ToggleSwitch from 'components/utils/ToggleSwitch';
import { FRONTEND_VERSION, CHAT_MODE, ENABLE_TOPIC } from 'config';
import { Topic } from 'types/topic';
import ChatHistory from 'components/chatapp/ChatHistory';
import sessionAPI from 'apis/sessionAPI';
import { useFetchData } from 'hooks/data';
import { MessageHistory, SessionHistory, SessionHistoryResponse } from 'types/session';
import { useNavigate, useLocation } from "react-router-dom";
import LoadingSpinner from 'components/utils/LoadingSpinner';
import logo from 'assets/images/LUCA_LOGO.png';
import { getUserDetail } from 'libs/token';

type ControlProps = {
  userId: string;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  sessionName: string;
  setSessionName: (sessionName: string) => void;
  topics: Topic[] | [];
  selectedTopic: string;
  setSelectedTopic: (currentState: string) => void;
  messages: MessageHistory[];
  sessionList: SessionHistory[];
  setSessionList: React.Dispatch<React.SetStateAction<SessionHistory[]>>
  blockControl: boolean
  isMobile?: boolean;
  isEnabledTAS: boolean;
  setIsEnabledTAS: React.Dispatch<React.SetStateAction<boolean>>;
  isEnabledTASExample: boolean;
  setIsEnabledTASExample: React.Dispatch<React.SetStateAction<boolean>>;
  isEnabledTSA: boolean;
  setIsEnabledTSA: React.Dispatch<React.SetStateAction<boolean>>;
  role: string;
  setIsGuardrailEnabled: React.Dispatch<React.SetStateAction<boolean>>;
  isEnabledGuardRail: boolean;
};

const Control: React.FC<ControlProps> = ({
  userId,
  sessionId,
  setSessionId,
  sessionName,
  setSessionName,
  topics,
  selectedTopic,
  setSelectedTopic,
  messages,
  sessionList,
  setSessionList,
  blockControl,
  isMobile = false,
  isEnabledTAS,
  setIsEnabledTAS,
  isEnabledTASExample,
  setIsEnabledTASExample,
  isEnabledTSA,
  setIsEnabledTSA,
  role,
  isEnabledGuardRail,
  setIsGuardrailEnabled
}) => {
  
  const { data: sessionListData, loading } = useFetchData<SessionHistoryResponse>(
    sessionAPI.getSessionHistory,
    [ENABLE_TOPIC ? selectedTopic : undefined, 30, null, CHAT_MODE]
  );

  const userDetail = getUserDetail();
  const options = topics.map((item) => ({
    displayName: item.instructorCode 
      ? `${item.topicCode} ${item.displayName} (${item.instructorCode})`
      : `${item.topicCode} ${item.displayName}`,
    name: item.name,
  }));

  // Guardrail enabled state is received from parent via props (`isEnabledGuardRail`)
  const shouldDisplayGuardRail = useMemo(() => {
    return role === 'admin';
  }, [role]);

  const { shouldDisplayTAS, shouldDisplayTSA } = useMemo(() => {
    const currentTopic = topics.find((topic) => topic.name === selectedTopic);
    return {
      shouldDisplayTAS: currentTopic?.displayTas ?? false,
      shouldDisplayTSA: currentTopic?.displayTsa ?? false,
    };
  }, [topics, selectedTopic]);

  const handleChange = (selectedDisplayName: string) => {
    if (!ENABLE_TOPIC) {
      return;
    }
    const selectedTopic = options.find(
      (option) => option.displayName === selectedDisplayName,
    );
    if (selectedTopic) {
      const newSessionId = uuidv4();
      setSelectedTopic(selectedTopic.name);
      setSessionId(newSessionId)
      setSessionName('')
      setIsEnabledTAS(false);
      setIsEnabledTASExample(false);
      setIsEnabledTSA(false);
    }
  };

  const openNewSession = () => {
    const lastUserMessage = messages.filter(message => message.chatType === 'user').pop();
    const sessionExists = sessionList.some(session => session.sessionId === sessionId);
    if (lastUserMessage) {
      if (!sessionExists) {
        setSessionList([{sessionId, sessionName, userMessage: lastUserMessage.message}, ...sessionList]);
      } else {
        const currentSession = sessionList.find(session => session.sessionId === sessionId);
        if (currentSession && currentSession.userMessage !== lastUserMessage.message) {
          const updatedSessionList = sessionList.filter(session => session.sessionId !== sessionId);
          const updatedSession = {
            sessionId,
            sessionName,
            userMessage: lastUserMessage.message
          };
          setSessionList([updatedSession, ...updatedSessionList]);
        }
      }
      const newSessionId = uuidv4()
      setSessionId(newSessionId);
      setSessionName('')
    }
  };

  useEffect(() => {
    if (sessionListData) {
      setSessionList(sessionListData.sessions);
    }
  }, [sessionListData]);

  const navigate = useNavigate();
  const location = useLocation();

  const handleLogOut = async () => {
    try {
      localStorage.setItem('redirectAfterLogin', location.pathname);
      localStorage.removeItem("token");
      navigate("/signin")

    } catch (err) {
      alert("Invalid credentials!");
    }
  };

  return (
    <div className={`px-2 flex flex-col ${isMobile ? 'h-full' : 'h-screen'} w-full`}>
      <div className="flex justify-center flex-shrink-0">
        <img src={logo} alt={"logo"} className="w-[120px] h-[113px] md:w-[150px] md:h-[141px] lg:w-[200px] lg:h-[188px] bg-transparent" />
      </div>
      <div className='-mt-4 flex-shrink-0'>
        <TextBox
          text={`Name:<br>${userDetail.nameTh}`}
          width="w-full"
          height="h-auto"
        />
      </div>
      {ENABLE_TOPIC && (
        <div className="p-1 flex-shrink-0">
          <DropDown
            options={options.map((option) => option.displayName)}
            width="w-full"
            height="h-[55px]"
            radius={24}
            onChange={handleChange}
            disabled={blockControl}
          />
        </div>
      )}
      {/* {shouldDisplayTAS && (
        <div className="p-1 flex-shrink-0">
          <ToggleSwitch
            label="TAS"
            isEnabled={isEnabledTAS}
            onToggle={() => setIsEnabledTAS(!isEnabledTAS)}
            disabled={blockControl}
            width="w-full"
            height="h-[55px]"
            radius={24}
          />
        </div>
      )}
      {shouldDisplayTAS && (
        <div className="p-1 flex-shrink-0">
          <ToggleSwitch
            label="TAS Example"
            isEnabled={isEnabledTASExample}
            onToggle={() => setIsEnabledTASExample(!isEnabledTASExample)}
            disabled={blockControl}
            width="w-full"
            height="h-[55px]"
            radius={24}
          />
        </div>
      )}
      {shouldDisplayTSA && (
        <div className="p-1 flex-shrink-0">
          <ToggleSwitch
            label="TSA"
            isEnabled={isEnabledTSA}
            onToggle={() => setIsEnabledTSA(!isEnabledTSA)}
            disabled={blockControl}
            width="w-full"
            height="h-[55px]"
            radius={24}
          />
        </div>
      )} */}
      {/* {shouldDisplayGuardRail && (
        <div className="p-1 flex-shrink-0">
          <ToggleSwitch
            label="Guardrail"
            isEnabled={isEnabledGuardRail}
            onToggle={() => setIsGuardrailEnabled(!isEnabledGuardRail)}
            disabled={blockControl}
            width="w-full"
            height="h-[55px]"
            radius={24}
          />
        </div>
      )} */}
      <div className="p-1 flex-shrink-0" onClick={blockControl ? undefined : openNewSession}>
        <Button text="New Chat" width="w-full" height="h-[55px]" radius={24} icon={<Plus />} disable={blockControl}/>
      </div>
      <div className="flex-1 p-1 overflow-hidden">
        {loading ? 
          <div className="flex justify-center items-center h-full pt-20">
            <LoadingSpinner />
          </div> : 
          <ChatHistory
            userDetail={userDetail}
            currentSessionId={sessionId}
            setSessionId={setSessionId}
            sessions={sessionList ? sessionList: []}
            setSessionList={setSessionList}
            disable={blockControl}
          />
        }
      </div>
      <div className="p-1 flex-shrink-0" onClick={blockControl ? undefined : handleLogOut}>
        <Button
          text="Sign out"
          width="w-full"
          height="h-[55px]"
          radius={24} 
          disable={blockControl}
        />
      </div>
      {/* <p className="mb-3 mx-2 text-BADBlue text-sm md:text-xl flex-shrink-0">(Experiment)</p> */}
      <div className="flex flex-col justify-center items-center flex-shrink-0">
        <p className="mb-3 text-BALBlue text-center w-full text-xs md:text-base">
          LUCA: Accountant Intelligence V{FRONTEND_VERSION}
        </p>
      </div>
    </div>
  );
};

export default Control;